<script setup lang="ts">
import { computed, useId } from 'vue'

// Native date / date-time / time picker with the UiTextField look. The value is the input's own text:
// "yyyy-MM-dd" (date), "yyyy-MM-ddTHH:mm" (datetime-local, in the browser's time zone) or "HH:mm" (time); '' = empty.
const props = withDefaults(
  defineProps<{
    label: string
    type?: 'date' | 'datetime-local' | 'month' | 'time'
    hint?: string
    error?: string
    min?: string
    max?: string
    disabled?: boolean
    required?: boolean
    // Filter bar look, the same as UiSelect's compact: 40px tall, quieter label.
    compact?: boolean
    id?: string
  }>(),
  { type: 'date' },
)

const model = defineModel<string>({ default: '' })

const generatedId = useId()
const inputId = computed(() => props.id ?? `date-${generatedId}`)
</script>

<template>
  <div class="ui-field">
    <label :for="inputId" class="ui-field__label" :class="{ 'ui-field__label--muted': compact }">{{ label }}</label>
    <input
      :id="inputId"
      v-model="model"
      class="ui-field__input"
      :class="{ 'ui-field__input--invalid': !!error, 'ui-field__input--compact': compact }"
      :type="type"
      :min="min"
      :max="max"
      :disabled="disabled"
      :required="required"
      :aria-invalid="!!error || undefined"
      :aria-describedby="error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined"
    />
    <span v-if="error" :id="`${inputId}-error`" role="alert" class="ui-field__error">{{ error }}</span>
    <span v-if="hint" :id="`${inputId}-hint`" class="ui-field__hint">{{ hint }}</span>
  </div>
</template>

<style scoped>
.ui-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.ui-field__label {
  font-size: var(--type-body-size);
  font-weight: var(--weight-medium);
  color: var(--text-label);
}
.ui-field__input {
  height: var(--field-height);
  padding: 0 12px;
  border: 1px solid var(--opaque-separator);
  border-radius: var(--radius-field);
  font-size: var(--type-body-size);
  color: var(--text-primary);
  background: var(--surface-card);
  width: 100%;
  color-scheme: inherit;
}
.ui-field__input::-webkit-calendar-picker-indicator {
  /* The browser's calendar/clock icon in the grey of the selects' arrow (15/10/2026: one look for every control). */
  opacity: 0.45;
  cursor: pointer;
}
.ui-field__input::-webkit-calendar-picker-indicator:hover {
  opacity: 0.75;
}
.ui-field__label--muted {
  font-weight: var(--weight-medium);
  color: var(--text-secondary);
}
.ui-field__input--compact {
  height: var(--field-height-compact);
}
.ui-field__input--invalid {
  border-color: var(--error);
}
.ui-field__input:disabled {
  opacity: 0.6;
}
.ui-field__error {
  font-size: var(--type-footnote-size);
  color: var(--error);
}
.ui-field__hint {
  font-size: var(--type-footnote-size);
  color: var(--text-secondary);
}
.ui-field__input:focus {
  outline: none;
  border-color: var(--accent);
}
</style>
